/* Copyright (c) 2023-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { from, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import {
    BatchSpanProcessor,
    BatchSpanProcessorBrowserConfig,
    WebTracerConfig,
    WebTracerProvider
} from '@opentelemetry/sdk-trace-web';
import { Resource } from '@opentelemetry/resources';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import { context, trace, defaultTextMapSetter, propagation, Context, TextMapGetter } from '@opentelemetry/api';
import { ZoneContextManager } from '@opentelemetry/context-zone-peer-dep';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { setGlobalErrorHandler } from '@opentelemetry/core';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import {
    LightStepTokenHeader,
    OpenTelemetrySpan,
    OpenTelemetryTracer,
    OTELTracerConfiguration,
    SpanTags
} from '../distributed-tracing';
import { CustomOTLPExporter } from './custom-otlp-exporter';

@Injectable({
    providedIn: 'root'
})
export class OTELTracerService {
    private logger: Logger;
    private provider;
    private initializationMap = new Map<string, boolean>();
    private tracer: OpenTelemetryTracer;
    private exporter: CustomOTLPExporter;
    private batchSpanProcessor: BatchSpanProcessor;

    constructor(private loggingService: LoggingService) {
        this.logger = this.loggingService.getLogger('OTELTracerService');
    }

    private initialize(options: OTELTracerConfiguration, disableTracingReplaySubject: ReplaySubject<boolean>): void {
        if (this.initializationMap.get(options.exportConfig.componentName)) {
            return;
        }

        const resource = new Resource({
            [SemanticResourceAttributes.SERVICE_NAME]: options.exportConfig.componentName
        });

        const tracerConfig: WebTracerConfig = {
            resource
        };
        this.provider = new WebTracerProvider(tracerConfig);

        const contextPropagator = new W3CTraceContextPropagator();
        propagation.setGlobalPropagator(contextPropagator);

        const zoneContextManager = new ZoneContextManager();
        zoneContextManager.enable();

        this.provider.register({
            propagator: contextPropagator,
            contextManager: zoneContextManager
        });

        const collectorOptions = {
            headers: {
                [LightStepTokenHeader]: options.exportConfig.accessToken
            },
            url: options.exportConfig.collectorUrl
        };
        this.exporter = new CustomOTLPExporter(collectorOptions, disableTracingReplaySubject);

        const otelErrorHandler = err => {
            console.error('Error while tracing', err);
        };

        setGlobalErrorHandler(otelErrorHandler);
        const config: BatchSpanProcessorBrowserConfig = {
            disableAutoFlushOnDocumentHide: true
        };
        this.batchSpanProcessor = new BatchSpanProcessor(this.exporter, config);
        this.provider.addSpanProcessor(this.batchSpanProcessor);
        this.initializationMap.set(options.exportConfig.componentName, true);
    }

    getSpanContextInfo(span: OpenTelemetrySpan): { [key: string]: string } {
        if (!this.tracer) {
            return null;
        }
        const spanContextInfo = {};
        const ctx = trace.setSpanContext(context.active(), span.spanContext());
        propagation.inject(ctx, spanContextInfo, defaultTextMapSetter);
        return spanContextInfo;
    }

    public getTraceInjectWrapper(): SpanTags {
        return this.getSpanContextInfo.bind(this);
    }

    createSpanUsingContextInfo(spanName: string, spanContextInfo): OpenTelemetrySpan {
        if (!this.tracer) {
            return null;
        }

        const activeContext = propagation.extract(context.active(), spanContextInfo);
        const span = this.tracer.startSpan(spanName, {}, activeContext);
        this.getSpanContextInfo(span);
        return span;
    }

    public getCreateSpanUsingContextWrapper(): OpenTelemetrySpan {
        return this.createSpanUsingContextInfo.bind(this);
    }

    public commitAnyPendingSpans(): Observable<boolean> {
        return from(this.batchSpanProcessor.forceFlush()).pipe(
            take(1),
            map(() => {
                this.logger.info('Spans flushed successfully:');
                return true;
            }),
            catchError(error => {
                this.logger.error('Failed to flush spans:', error);
                return of(false);
            })
        );
    }

    createSpan(spanName: string, parentSpan?: OpenTelemetrySpan): OpenTelemetrySpan {
        if (!this.tracer) {
            this.logger.error('Open telemetry tracer not initialized yet, returning null for span name', spanName);
            return null;
        }

        if (!parentSpan) {
            return this.tracer.startSpan(spanName);
        }

        const ctx = trace.setSpan(context.active(), parentSpan);
        return this.tracer.startSpan(spanName, {}, ctx);
    }

    getOTELTracer(
        options: OTELTracerConfiguration,
        disableOpenTracingReplaySubject: ReplaySubject<boolean>
    ): OpenTelemetryTracer {
        this.initialize(options, disableOpenTracingReplaySubject);
        this.tracer = this.provider.getTracer();
        return this.tracer;
    }

    extractContext<Carrier>(spanData: Carrier, spanContext?: Context, getter?: TextMapGetter<Carrier>): Context {
        const propagationContext = spanContext ? spanContext : context.active();

        const extractedContext = propagation.extract(propagationContext, spanData, getter);

        return extractedContext;
    }

    // disabled linting to prevent the linter and formatter from fighting with the "F extends (...args: A)"
    // eslint-disable-next-line space-before-function-paren
    runWithContext<A extends unknown[], F extends (...args: A) => ReturnType<F>>(
        spanContext: Context,
        fn: F,
        thisArg?: ThisParameterType<F>,
        ...args: A
    ): ReturnType<F> {
        return context.with(spanContext, fn, thisArg, ...args);
    }
}
